import { readFile, stat, writeFile } from "node:fs/promises";
import path from "node:path";

import { ensureDir } from "../lib/fs.js";
import { buildImportGraph } from "../lib/import-graph.js";
import { buildUnifiedDiff } from "../lib/text-diff.js";
import {
  createCopyId,
  ensureSafeTargetPath,
  fileExists,
  getDependencyVersion,
  getOrCreateDependencySection,
  isBinaryBuffer,
  parseManifest,
  readJsonFile,
  resolveManifestCandidateFiles,
  resolveSingleExportDirectory,
  sortDependencySections,
  toProjectRelative,
  type PackageJsonRecord,
} from "./copy-service-shared.js";
import {
  buildConflictReportArtifact,
  buildExportManifestArtifact,
  buildReferenceExportArtifact,
} from "./runtime-guidance.js";
import type {
  PrimeUiCopyConflictReport,
  PrimeUiCopyPageResult,
  PrimeUiDependencyToAdd,
  PrimeUiDependencyVersionConflict,
  PrimeUiRuntimeGuidance,
} from "../types.js";
type CopyPageServiceInput = {
  projectRoot: string;
  exportsRoot: string;
  originPageSlug: string;
};

function normalizeSlug(slug: string): string {
  const trimmed = slug.trim();
  if (!trimmed || trimmed === "/") {
    return "/";
  }

  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return withLeadingSlash.replace(/\/+$/, "") || "/";
}

function resolveTargetFilePath(input: {
  sourceFilePath: string;
  sourcePagePath: string;
  sourceComponentsPath: string;
  targetPagePath: string;
  targetComponentsPath: string;
  exportRoot: string;
  projectRoot: string;
}): string {
  const {
    sourceFilePath,
    sourcePagePath,
    sourceComponentsPath,
    targetPagePath,
    targetComponentsPath,
    exportRoot,
    projectRoot,
  } = input;

  if (sourceFilePath === sourcePagePath) {
    return targetPagePath;
  }

  const componentsPrefix = `${sourceComponentsPath}${path.sep}`;
  if (
    sourceFilePath === sourceComponentsPath ||
    sourceFilePath.startsWith(componentsPrefix)
  ) {
    const relativeToComponents = path.relative(
      sourceComponentsPath,
      sourceFilePath,
    );
    return path.join(targetComponentsPath, relativeToComponents);
  }

  const relativeToExport = path.relative(exportRoot, sourceFilePath);
  if (relativeToExport.startsWith("..") || path.isAbsolute(relativeToExport)) {
    throw new Error(`Source file is outside export root: ${sourceFilePath}`);
  }

  return path.join(projectRoot, relativeToExport);
}

function buildPageCopyGuidance(input: {
  status: PrimeUiCopyPageResult["status"];
  exportPath: string;
  manifestPath: string;
  reportPath: string;
  hasFileConflicts: boolean;
  addedDependencies: PrimeUiDependencyToAdd[];
}): PrimeUiRuntimeGuidance {
  const artifacts = [
    buildExportManifestArtifact(input.manifestPath),
    buildReferenceExportArtifact(input.exportPath),
  ];

  if (input.hasFileConflicts) {
    artifacts.push(buildConflictReportArtifact(input.reportPath));
  }

  const installDependenciesPrefix =
    input.addedDependencies.length > 0
      ? "Install any newly added dependencies first. "
      : "";

  let requiredActions =
    "Follow repository instructions for verification first. If repository rules are absent, inspect package.json scripts and run the most relevant minimal checks. Compare against the reference_export artifact only if behavior or integration looks wrong.";

  if (input.status === "needs_review" && input.hasFileConflicts) {
    requiredActions =
      "Review every file listed in the conflict_report artifact. Those conflicting files are an inseparable part of the imported page functionality and must be resolved deliberately before the transfer is complete. After resolving them, run repository-required verification and re-check after any local adaptation or refactor.";
  } else if (input.status === "needs_review") {
    requiredActions =
      "Resolve dependency version conflicts or other review blockers first, then run repository-required verification. If repository rules are absent, inspect package.json scripts and run the most relevant minimal checks. Use the reference_export artifact only when behavior diverges or local adaptation is unclear.";
  }

  return {
    artifacts,
    requiredActions: `${installDependenciesPrefix}${requiredActions}`,
  };
}

export async function copyPageFromExport(
  input: CopyPageServiceInput,
): Promise<PrimeUiCopyPageResult> {
  const normalizedOriginSlug = normalizeSlug(input.originPageSlug);

  const { exportId, exportPath } = await resolveSingleExportDirectory(
    input.exportsRoot,
  );
  const manifestPath = path.join(
    input.exportsRoot,
    `${exportId}.manifest.json`,
  );

  if (!(await fileExists(manifestPath))) {
    throw new Error(
      `Export manifest is missing at ${manifestPath}. Run create_export and download_export to regenerate it.`,
    );
  }

  const manifest = parseManifest(await readJsonFile<unknown>(manifestPath));
  if (manifest.export.id !== exportId) {
    throw new Error(
      `Manifest export id mismatch: expected ${exportId}, got ${manifest.export.id}. Re-run create_export and download_export.`,
    );
  }

  const page = manifest.pages.find(
    (item) => normalizeSlug(item.slug) === normalizedOriginSlug,
  );
  if (!page) {
    throw new Error(
      `Page not found in manifest for slug: ${normalizedOriginSlug}`,
    );
  }

  const sourcePagePath = path.join(exportPath, page.pagePath);
  const sourceComponentsPath = path.join(exportPath, page.componentsPath);

  const sourcePageStats = await stat(sourcePagePath).catch(() => null);
  if (!sourcePageStats?.isFile()) {
    throw new Error(`Source page file not found: ${sourcePagePath}`);
  }

  const sourceComponentsStats = await stat(sourceComponentsPath).catch(
    () => null,
  );
  if (!sourceComponentsStats?.isDirectory()) {
    throw new Error(
      `Source components folder not found: ${sourceComponentsPath}`,
    );
  }

  const targetPagePath = path.join(input.projectRoot, page.pagePath);
  const targetComponentsPath = path.join(
    input.projectRoot,
    page.componentsPath,
  );

  ensureSafeTargetPath(input.projectRoot, targetPagePath);
  ensureSafeTargetPath(input.projectRoot, targetComponentsPath);

  const candidateFiles = await resolveManifestCandidateFiles({
    exportPath,
    manifestOwnerLabel: `page "${normalizedOriginSlug}"`,
    files: page.manifest.files,
  });
  const dependencyGraph = await buildImportGraph({
    projectRoot: exportPath,
    entryFiles: candidateFiles,
    followInternalImports: false,
  });

  const newFiles: PrimeUiCopyPageResult["newFiles"] = [];
  const identicalFiles: PrimeUiCopyPageResult["identicalFiles"] = [];
  const conflictFiles: PrimeUiCopyPageResult["conflictFiles"] = [];
  const conflictReportEntries: PrimeUiCopyConflictReport["conflicts"] = [];

  for (const sourceFilePath of candidateFiles) {
    const targetFilePath = resolveTargetFilePath({
      sourceFilePath,
      sourcePagePath,
      sourceComponentsPath,
      targetPagePath,
      targetComponentsPath,
      exportRoot: exportPath,
      projectRoot: input.projectRoot,
    });
    ensureSafeTargetPath(input.projectRoot, targetFilePath);

    const sourceBuffer = await readFile(sourceFilePath);
    const plannedSourceBuffer = sourceBuffer;
    let targetBuffer: Buffer | null = null;
    try {
      targetBuffer = await readFile(targetFilePath);
    } catch {
      targetBuffer = null;
    }

    const sourceRelative = toProjectRelative(exportPath, sourceFilePath);
    const targetRelative = toProjectRelative(input.projectRoot, targetFilePath);

    if (!targetBuffer) {
      await ensureDir(path.dirname(targetFilePath));
      await writeFile(targetFilePath, plannedSourceBuffer);
      newFiles.push(targetRelative);
      continue;
    }

    if (plannedSourceBuffer.equals(targetBuffer)) {
      identicalFiles.push(targetRelative);
      continue;
    }

    const isBinary =
      isBinaryBuffer(plannedSourceBuffer) || isBinaryBuffer(targetBuffer);
    const diff = isBinary
      ? "Binary files differ."
      : buildUnifiedDiff({
          oldText: targetBuffer.toString("utf-8"),
          newText: plannedSourceBuffer.toString("utf-8"),
          oldLabel: `user/${targetRelative}`,
          newLabel: `export/${sourceRelative}`,
        });

    conflictReportEntries.push({
      sourcePath: sourceRelative,
      targetPath: targetRelative,
      diff,
      isBinary,
    });
  }

  const exportPackageJsonPath = path.join(exportPath, "package.json");
  const userPackageJsonPath = path.join(input.projectRoot, "package.json");
  if (!(await fileExists(exportPackageJsonPath))) {
    throw new Error(`Export package.json not found: ${exportPackageJsonPath}`);
  }
  if (!(await fileExists(userPackageJsonPath))) {
    throw new Error(`User package.json not found: ${userPackageJsonPath}`);
  }

  const exportPackageJson = await readJsonFile<PackageJsonRecord>(
    exportPackageJsonPath,
  );
  const userPackageJson =
    await readJsonFile<PackageJsonRecord>(userPackageJsonPath);

  const addedDependencies: PrimeUiDependencyToAdd[] = [];
  const dependenciesVersionConflicts: PrimeUiDependencyVersionConflict[] = [];

  for (const packageName of dependencyGraph.externalPackages) {
    const exportDependency = getDependencyVersion(
      exportPackageJson,
      packageName,
    );
    if (!exportDependency) {
      continue;
    }

    const userDependency = getDependencyVersion(userPackageJson, packageName);
    if (!userDependency) {
      const sectionData = getOrCreateDependencySection(
        userPackageJson,
        exportDependency.section,
      );
      sectionData[packageName] = exportDependency.version;
      addedDependencies.push({
        packageName,
        version: exportDependency.version,
        section: exportDependency.section,
      });
      continue;
    }

    if (userDependency.version !== exportDependency.version) {
      dependenciesVersionConflicts.push({
        packageName,
        section: exportDependency.section,
        exportVersion: exportDependency.version,
        userVersion: userDependency.version,
      });
    }
  }

  if (addedDependencies.length > 0) {
    sortDependencySections(userPackageJson);
    await writeFile(
      userPackageJsonPath,
      `${JSON.stringify(userPackageJson, null, 2)}\n`,
      "utf-8",
    );
  }

  const sortedAddedDependencies = addedDependencies.sort((a, b) =>
    a.packageName.localeCompare(b.packageName),
  );
  const sortedDependenciesVersionConflicts = dependenciesVersionConflicts.sort(
    (a, b) => a.packageName.localeCompare(b.packageName),
  );
  const sortedConflictReportEntries = conflictReportEntries.sort((a, b) =>
    a.targetPath.localeCompare(b.targetPath),
  );

  for (const entry of sortedConflictReportEntries) {
    conflictFiles.push({
      targetPath: entry.targetPath,
      isBinary: entry.isBinary,
    });
  }

  const copyId = createCopyId();
  const reportPath = path.join(
    input.exportsRoot,
    `${exportId}.copy-report-${copyId}.json`,
  );
  const report: PrimeUiCopyConflictReport = {
    reportPath,
    generatedAt: new Date().toISOString(),
    exportId,
    originPageSlug: normalizedOriginSlug,
    sourcePagePath: page.pagePath,
    sourceComponentsPath: page.componentsPath,
    conflicts: sortedConflictReportEntries,
  };
  await writeFile(reportPath, `${JSON.stringify(report, null, 2)}\n`, "utf-8");

  const hasFileConflicts = conflictFiles.length > 0;
  const hasDependencyVersionConflicts =
    sortedDependenciesVersionConflicts.length > 0;
  const needsReview = hasFileConflicts || hasDependencyVersionConflicts;
  const status: PrimeUiCopyPageResult["status"] = needsReview
    ? "needs_review"
    : "completed";
  const messageParts: string[] = [];
  if (hasFileConflicts) {
    messageParts.push(
      `Conflicts detected. Manual resolution is required before any follow-up integration steps. Review ${reportPath} for full diffs and resolve conflicts based on the target project behavior, the imported page functionality, and the possibility that MCP may run against third-party projects not generated by PrimeUI.`,
    );
  } else if (hasDependencyVersionConflicts) {
    messageParts.push(
      `Dependency version conflicts detected. Manual resolution is required before any follow-up integration steps. Review ${reportPath} for full local report details, inspect dependenciesVersionConflicts in this response, and resolve versions based on the target project behavior and the imported page functionality.`,
    );
  } else {
    messageParts.push(
      `Copy completed with no file conflicts or dependency version conflicts. Review ${reportPath} for full local details if needed.`,
    );
  }
  if (sortedAddedDependencies.length > 0) {
    messageParts.push(
      "Missing dependencies were added to package.json but are NOT installed yet. Run dependency installation manually with the package manager used by the target project.",
    );
  }

  const guidance = buildPageCopyGuidance({
    status,
    exportPath,
    manifestPath,
    reportPath,
    hasFileConflicts,
    addedDependencies: sortedAddedDependencies,
  });

  return {
    status,
    message: messageParts.join(" "),
    reportPath,
    exportId,
    exportPath,
    originPageSlug: normalizedOriginSlug,
    sourcePagePath: page.pagePath,
    sourceComponentsPath: page.componentsPath,
    newFiles,
    identicalFiles,
    conflictFiles,
    addedDependencies: sortedAddedDependencies,
    dependenciesVersionConflicts: sortedDependenciesVersionConflicts,
    summary: {
      totalCandidateFiles: candidateFiles.length,
      copiedFiles: newFiles.length,
      identicalFiles: identicalFiles.length,
      conflictFiles: conflictFiles.length,
      addedDependencies: addedDependencies.length,
      dependenciesVersionConflicts: dependenciesVersionConflicts.length,
    },
    guidance,
  };
}
